import React from 'react';
import {
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Apps as AppsIcon,
  Assessment as AssessmentIcon,
  Engineering as EngineeringIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { Message } from '../../stores/chatStore';

interface PanelOpenerProps {
  message: Message;
  onOpenPanel?: (panelType: string, metadata: any) => void;
}

const PanelOpener: React.FC<PanelOpenerProps> = ({ message, onOpenPanel }) => {
  // Determine which panel type based on message metadata
  const getPanelInfo = () => {
    const metadata = message.metadata || {};
    
    // HVAC Panel
    if (metadata.hvacCalculationId) {
      return {
        type: 'hvac',
        icon: <EngineeringIcon fontSize="small" />,
        tooltip: 'Open HVAC Calculation Panel',
        data: {
          calculationId: metadata.hvacCalculationId,
          projectId: metadata.projectId,
        }
      };
    }
    
    // Electrical Panel (future)
    if (metadata.electricalCalculationId) {
      return {
        type: 'electrical',
        icon: <AssessmentIcon fontSize="small" />,
        tooltip: 'Open Electrical Analysis Panel',
        data: {
          calculationId: metadata.electricalCalculationId,
          projectId: metadata.projectId,
        }
      };
    }
    
    // MEP Coordination Panel (future)
    if (metadata.mepCoordinationId) {
      return {
        type: 'mep-coordination',
        icon: <AppsIcon fontSize="small" />,
        tooltip: 'Open MEP Coordination Panel',
        data: {
          coordinationId: metadata.mepCoordinationId,
          projectId: metadata.projectId,
        }
      };
    }
    
    // Default: Generic app panel for HTML content
    if (message.content_type === 'html' && !metadata.hvacCalculationId) {
      return {
        type: 'app',
        icon: <OpenInNewIcon fontSize="small" />,
        tooltip: 'Open in Panel',
        data: {
          messageId: message.id,
          content: message.content,
        }
      };
    }
    
    return null;
  };

  const panelInfo = getPanelInfo();
  
  // Don't render if no panel is available for this message
  if (!panelInfo) {
    return null;
  }

  const handleClick = () => {
    if (onOpenPanel) {
      onOpenPanel(panelInfo.type, panelInfo.data);
    } else {
      // Fallback: trigger custom event for parent components to handle
      const event = new CustomEvent('openPanel', {
        detail: {
          type: panelInfo.type,
          data: panelInfo.data,
          message: message
        }
      });
      window.dispatchEvent(event);
    }
  };

  return (
    <Tooltip title={panelInfo.tooltip}>
      <IconButton 
        size="small" 
        onClick={handleClick} 
        color="primary"
        sx={{
          '&:hover': {
            backgroundColor: 'primary.light',
            color: 'primary.contrastText',
          }
        }}
      >
        {panelInfo.icon}
      </IconButton>
    </Tooltip>
  );
};

export default PanelOpener;